// VIBRANIAM-SPACE v2 - Viewport.jsx
// Owns the WebGL renderer and drives Engine.step() once per animation frame.
// The engine does the layers, feedback and grading; this component only feeds
// it audio and hands back statistics.

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Engine } from '../engine/Engine.js';
import { smoothTowards } from '../utils/particle-utils.js';

export default function Viewport({ graph, audio, running = true, onStats, onLiveParams, onContextLost, onCanvasReady }) {
  const mountRef = useRef(null);
  const engineRef = useRef(null);
  const graphRef = useRef(graph);
  const audioRef = useRef(audio);
  const runningRef = useRef(running);
  const cbRef = useRef({ onStats, onLiveParams, onContextLost, onCanvasReady });
  const lastSyncedGraphRef = useRef(null);

  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  graphRef.current = graph;
  audioRef.current = audio;
  runningRef.current = running;
  cbRef.current = { onStats, onLiveParams, onContextLost, onCanvasReady };

  useEffect(() => {
    let disposed = false;
    const mount = mountRef.current;
    if (!mount) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: false,           // the bloom pass hides aliasing better than MSAA
        alpha: false,
        powerPreference: 'high-performance',
      });
    } catch (err) {
      setError(`WebGL non disponibile: ${err.message}`);
      return undefined;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(dpr);
    const w0 = mount.clientWidth || 960;
    const h0 = mount.clientHeight || 540;
    renderer.setSize(w0, h0, false);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    mount.appendChild(renderer.domElement);
    cbRef.current.onCanvasReady?.(renderer.domElement);

    // A lost context (VRAM exhaustion, driver reset, GPU switch) otherwise
    // leaves the canvas frozen on its last frame with no recovery. preventDefault
    // keeps the browser willing to restore it, but rebuilding a Three.js scene
    // graph of this size in place is fragile - instead we tear this instance
    // down and let the parent remount a fresh one (fresh canvas, fresh context).
    const handleContextLost = (e) => {
      e.preventDefault();
      cancelAnimationFrame(raf);
      setError('Contesto grafico perso (probabile esaurimento memoria video). Ripristino...');
      cbRef.current.onContextLost?.();
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false);

    const engine = new Engine({
      renderer,
      width: Math.round(w0 * dpr),
      height: Math.round(h0 * dpr),
    });
    engineRef.current = engine;
    // Inspection handle, same idea as window.__VS_STATE__ in render-entry.js:
    // lets a console or an automated check read real engine state (uniforms,
    // stats, buffers) instead of inferring it from pixels. Set here so it exists
    // even when rAF is paused (hidden tab), and refreshed in the loop below so
    // it always names the engine that is actually stepping.
    window.__VS_ENGINE__ = engine;

    const ro = new ResizeObserver(() => {
      const w = mount.clientWidth || 960;
      const h = mount.clientHeight || 540;
      renderer.setSize(w, h, false);
      engine.resize(Math.round(w * dpr), Math.round(h * dpr));
    });
    ro.observe(mount);

    let raf = 0;
    let fpsAvg = 60;
    let last = performance.now();
    let statTimer = 0;

    const loop = () => {
      raf = requestAnimationFrame(loop);
      const now = performance.now();
      const wall = Math.min(0.25, (now - last) / 1000);
      last = now;

      const eng = engineRef.current;
      if (!eng || !eng.ready) return;

      // Published from inside the loop so it always names the engine that is
      // actually stepping, never one left behind by a hot-reload remount.
      if (window.__VS_ENGINE__ !== eng) window.__VS_ENGINE__ = eng;

      // Pushed at most once per rendered frame - the graph can change many
      // times per second while dragging a node (once per pointermove), and
      // the engine never needs to see faster than the display can redraw.
      if (graphRef.current !== lastSyncedGraphRef.current) {
        eng.setGraph(graphRef.current);
        lastSyncedGraphRef.current = graphRef.current;
      }

      if (runningRef.current) {
        const features = audioRef.current?.tick ? audioRef.current.tick(now) : null;
        eng.step(features || undefined, null);
      }

      fpsAvg = smoothTowards(fpsAvg, wall > 0 ? 1 / wall : 60, 4, wall);
      statTimer += wall;
      if (statTimer > 0.18) {
        statTimer = 0;
        const s = eng.lastStats;
        cbRef.current.onStats?.({
          fps: fpsAvg,
          alive: s.alive, capacity: s.capacity,
          layers: s.layers, frame: s.frame,
          width: eng.width, height: eng.height,
        });
        // Resolved parameter values, so the sliders can show where modulation
        // has actually pushed them.
        if (cbRef.current.onLiveParams) {
          const live = {};
          for (const n of eng.runtime.graph.nodes) live[n.id] = eng.runtime.resolveParams(n);
          cbRef.current.onLiveParams(live);
        }
      }
    };

    (async () => {
      try {
        await engine.init();
        if (disposed) return;
        engine.setGraph(graphRef.current);
        lastSyncedGraphRef.current = graphRef.current;
        setReady(true);
        raf = requestAnimationFrame(loop);
      } catch (err) {
        if (!disposed) setError(String(err?.message || err));
      }
    })();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener('webglcontextlost', handleContextLost, false);
      cbRef.current.onCanvasReady?.(null);
      try { engine.dispose(); } catch { /* context may already be gone */ }
      try { renderer.dispose(); } catch { /* context may already be gone */ }
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      if (window.__VS_ENGINE__ === engine) delete window.__VS_ENGINE__;
      engineRef.current = null;
    };
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden bg-ink-900">
      <div ref={mountRef} className="h-full w-full" />
      {!ready && !error && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-slate-400">
          caricamento motore (WASM + WebGL)...
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <div className="max-w-md rounded-lg border border-red-500/40 bg-red-950/40 p-4 text-xs text-red-200">
            <div className="mb-1 font-semibold">Motore non avviato</div>
            <div className="mb-2 opacity-80">{error}</div>
            <div className="opacity-70">
              Se mancano i moduli WASM: <code className="text-red-100">npm run build-wasm</code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function useEngineHandle() {
  return useRef(null);
}
